import { ApiProject, ApiSimpleTenant, ApiTenantProvider } from "@incendium/api";
import { Close } from "@mui/icons-material";
import {
  Box,
  Button,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  Stack,
  Switch,
  Typography,
} from "@mui/material";
import CustomDialog from "Components/CustomDialog/CustomDialog";
import Loading from "Components/Loading/Loading";
import { AllProjectsSelector } from "features/project";
import useAllProjects from "features/project/hooks/useAllProjects";
import {
  buildSamlTenantBody,
  createSamlTenant,
  getSamlTenantProjectLinks,
  updateSamlTenant,
} from "features/sso/samlService";
import { useNotification } from "Hooks";
import { useSamlTenants } from "Hooks/useSamlTenants";
import { CallbackOrVal } from "Interfaces";
import { useCallback, useEffect, useState } from "react";
import { TextValidator, ValidatorForm } from "react-material-ui-form-validator";

function resolveProjectsForLinks(
  links: Array<{ clientId: number; projectId: number }>,
  allProjects: ApiProject[],
): ApiProject[] {
  return links.map((l) => {
    const p = allProjects.find(
      (x) => x.id === l.projectId && x.clientId === l.clientId,
    );
    if (p) return p;
    return {
      id: l.projectId,
      clientId: l.clientId,
      name: `Project #${l.projectId}`,
    } as ApiProject;
  });
}

export interface SamlTenantDialogProps {
  open: boolean;
  onClose: () => void;
  /** When set, dialog opens in edit mode with this tenant */
  tenantToEdit: ApiSimpleTenant | null;
}

function SamlTenantDialog({
  open,
  onClose,
  tenantToEdit,
}: SamlTenantDialogProps) {
  const { refetchTenants } = useSamlTenants();
  const { allProjects } = useAllProjects();
  const { showSuccessNotification, showErrorNotification } = useNotification();

  const [entityId, setEntityId] = useState("");
  const [metaDataUrl, setMetaDataUrl] = useState("");
  const [domain, setDomain] = useState("");
  const [name, setName] = useState("");
  const [enabled, setEnabled] = useState(true);
  const [linkedProjects, setLinkedProjects] = useState<ApiProject[]>([]);
  const [addProjectKey, setAddProjectKey] = useState(0);
  const [saving, setSaving] = useState(false);

  const isEdit = Boolean(tenantToEdit);

  useEffect(() => {
    if (!open) return;
    if (!tenantToEdit) {
      setEntityId("");
      setMetaDataUrl("");
      setDomain("");
      setName("");
      setEnabled(true);
      setLinkedProjects([]);
      setAddProjectKey((k) => k + 1);
      return;
    }
    setEntityId(tenantToEdit.entityId || "");
    setMetaDataUrl(tenantToEdit.metaDataUrl || "");
    setDomain(tenantToEdit.domain || "");
    setName(tenantToEdit.name || "");
    setEnabled(tenantToEdit.enabled !== false);
    setLinkedProjects(
      resolveProjectsForLinks(
        getSamlTenantProjectLinks(tenantToEdit),
        allProjects,
      ),
    );
    setAddProjectKey((k) => k + 1);
  }, [open, tenantToEdit, allProjects]);

  const onAddProject = (v: CallbackOrVal<ApiProject | null>) => {
    const p = typeof v === "function" ? v(null) : v;
    if (!p?.id || p.clientId == null) return;
    setLinkedProjects((prev) =>
      prev.some((x) => x.id === p.id && x.clientId === p.clientId)
        ? prev
        : [...prev, p],
    );
    setAddProjectKey((k) => k + 1);
  };

  const onRemoveProject = (p: ApiProject) => {
    setLinkedProjects((prev) =>
      prev.filter((x) => !(x.id === p.id && x.clientId === p.clientId)),
    );
  };

  const handleSubmit = useCallback(async () => {
    const projectLinks = linkedProjects
      .filter((p) => p.id != null && p.clientId != null)
      .map((p) => ({ clientId: p.clientId!, projectId: p.id! }));
    if (projectLinks.length === 0) {
      showErrorNotification("Add at least one project");
      return;
    }
    setSaving(true);
    try {
      const body = buildSamlTenantBody({
        entityId,
        metaDataUrl,
        domain,
        name: name || (domain ? domain.split(".")[0] : ""),
        enabled,
        projectLinks,
        provider: ApiTenantProvider.TENANT_PROVIDER_MICROSOFT,
      });
      if (tenantToEdit) {
        if (!tenantToEdit.id) {
          showErrorNotification("Missing internal tenant id");
          return;
        }
        await updateSamlTenant(tenantToEdit.id, body);
        showSuccessNotification("SSO tenant updated");
      } else {
        await createSamlTenant(body);
        showSuccessNotification("SSO tenant created");
      }
      await refetchTenants(true);
      onClose();
    } catch {
      showErrorNotification("Could not save SSO tenant");
    } finally {
      setSaving(false);
    }
  }, [
    linkedProjects,
    entityId,
    metaDataUrl,
    domain,
    name,
    enabled,
    tenantToEdit,
    showSuccessNotification,
    showErrorNotification,
    refetchTenants,
    onClose,
  ]);

  const title = isEdit ? "Edit SSO tenant" : "New SSO tenant";

  return (
    <CustomDialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <Box sx={{ position: "relative" }}>
        <ValidatorForm onSubmit={handleSubmit}>
          <DialogTitle>{title}</DialogTitle>
          {saving && (
            <Box
              sx={{
                position: "absolute",
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                backgroundColor: "rgba(255, 255, 255, 0.5)",
                zIndex: 1,
              }}
            >
              <Loading />
            </Box>
          )}
          <DialogContent>
            <Stack spacing={2}>
              <TextValidator
                variant="outlined"
                fullWidth
                name="samlEntityId"
                label="Enterprise app name (entity ID) *"
                helperText="e.g. incendium — the name given when creating the app in Microsoft Entra"
                value={entityId}
                disabled={isEdit || saving}
                validators={["required"]}
                errorMessages={["Entity ID is required"]}
                size="small"
                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                  setEntityId(e.target.value)
                }
              />
              <TextValidator
                variant="outlined"
                fullWidth
                name="samlMetaDataUrl"
                label={
                  isEdit
                    ? "Federation metadata URL"
                    : "Federation metadata URL (optional)"
                }
                helperText={
                  isEdit
                    ? "The federation metadata link from your IdP (Microsoft Entra enterprise app)"
                    : "Leave empty until your client sends the Federation metadata URL"
                }
                value={metaDataUrl}
                disabled={saving}
                validators={["isStoreUrl"]}
                errorMessages={["Enter a valid URL (https://…)"]}
                size="small"
                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                  setMetaDataUrl(e.target.value)
                }
              />
              <TextValidator
                variant="outlined"
                fullWidth
                name="samlDomain"
                label="Email domain *"
                helperText="e.g. example.com for users @example.com"
                value={domain}
                disabled={saving}
                validators={["required"]}
                errorMessages={["Domain is required"]}
                size="small"
                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                  setDomain(e.target.value)
                }
              />
              <TextValidator
                variant="outlined"
                fullWidth
                name="samlName"
                label="Display name"
                value={name}
                disabled={saving}
                size="small"
                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                  setName(e.target.value)
                }
              />
              <FormControlLabel
                control={
                  <Switch
                    checked={enabled}
                    onChange={(_, v) => setEnabled(v)}
                    disabled={saving}
                  />
                }
                label="Enabled"
              />
              <Typography variant="subtitle2">Projects</Typography>
              <Typography
                variant="caption"
                color="text.secondary"
                display="block"
              >
                SSO users from this IdP get project admin on each project listed
                (no group claim; group is empty). Add multiple projects or
                remove ones you no longer need.
              </Typography>
              <Stack spacing={1}>
                {linkedProjects.map((p) => (
                  <Box
                    key={`${p.clientId}-${p.id}`}
                    display="flex"
                    alignItems="center"
                    justifyContent="space-between"
                    sx={{
                      pl: 1,
                      pr: 0.5,
                      py: 0.5,
                      borderRadius: 1,
                      bgcolor: "action.hover",
                    }}
                  >
                    <Typography variant="body2">
                      {p.name}
                      {p.client?.businessName != null
                        ? ` · ${p.client.businessName}`
                        : ""}
                    </Typography>
                    <IconButton
                      size="small"
                      aria-label="Remove project"
                      onClick={() => onRemoveProject(p)}
                      disabled={saving}
                    >
                      <Close fontSize="small" />
                    </IconButton>
                  </Box>
                ))}
              </Stack>
              <AllProjectsSelector
                key={addProjectKey}
                project={null}
                setProject={onAddProject}
                filterFn={(proj) =>
                  !linkedProjects.some(
                    (x) => x.id === proj.id && x.clientId === proj.clientId,
                  )
                }
                variant="outlined"
                disabled={saving}
              />
            </Stack>
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2 }}>
            <Button onClick={onClose} disabled={saving}>
              Cancel
            </Button>
            <Button
              type="submit"
              variant="contained"
              color="primary"
              disabled={saving}
            >
              {saving ? "Saving…" : "Save"}
            </Button>
          </DialogActions>
        </ValidatorForm>
      </Box>
    </CustomDialog>
  );
}

export default SamlTenantDialog;
